import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/pl';
import 'dayjs/locale/en';
import { FiCalendar, FiX } from 'react-icons/fi';
import CalendarDropdown from './CalendarDropdown';
import './CustomDatePicker.css';

const CustomDatePicker = ({
    value,
    onChange,
    min,
    max,
    name,
    className = '',
    disabled = false,
    placeholder,
    required = false,
    id,
}) => {
    const { t, i18n } = useTranslation('common');
    const currentLang = i18n.language?.substring(0, 2) || 'pl';
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownStyle, setDropdownStyle] = useState({});
    const [isMobile, setIsMobile] = useState(false);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);

    const resolvedPlaceholder = placeholder || t('datepicker.selectDate');

    // Set dayjs locale based on i18n language
    useEffect(() => {
        dayjs.locale(currentLang);
    }, [currentLang]);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 480);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const positionDropdown = useCallback(() => {
        if (!inputRef.current || isMobile) return;

        const rect = inputRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const dropdownHeight = 380;

        const openBelow = spaceBelow >= dropdownHeight || spaceBelow >= spaceAbove;

        setDropdownStyle({
            position: 'fixed',
            left: `${rect.left}px`,
            width: `${Math.max(rect.width, 320)}px`,
            ...(openBelow
                ? { top: `${rect.bottom + 4}px` }
                : { bottom: `${window.innerHeight - rect.top + 4}px` }),
            zIndex: 99999,
        });
    }, [isMobile]);

    const handleOpen = useCallback(() => {
        if (disabled) return;
        positionDropdown();
        setIsOpen(true);
    }, [disabled, positionDropdown]);

    const handleClose = useCallback(() => {
        setIsOpen(false);
    }, []);

    const handleSelect = useCallback((dateStr) => {
        const syntheticEvent = {
            target: { name: name || '', value: dateStr },
            currentTarget: { name: name || '', value: dateStr },
        };
        onChange(syntheticEvent);
        setIsOpen(false);
    }, [name, onChange]);

    const handleClear = useCallback((e) => {
        e.stopPropagation();
        const syntheticEvent = {
            target: { name: name || '', value: '' },
            currentTarget: { name: name || '', value: '' },
        };
        onChange(syntheticEvent);
    }, [name, onChange]);

    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (e) => {
            if (
                inputRef.current && !inputRef.current.contains(e.target) &&
                dropdownRef.current && !dropdownRef.current.contains(e.target)
            ) {
                handleClose();
            }
        };

        const handleEscape = (e) => {
            if (e.key === 'Escape') handleClose();
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, handleClose]);

    useEffect(() => {
        if (isOpen && !isMobile) {
            positionDropdown();
            window.addEventListener('scroll', positionDropdown, true);
            window.addEventListener('resize', positionDropdown);
            return () => {
                window.removeEventListener('scroll', positionDropdown, true);
                window.removeEventListener('resize', positionDropdown);
            };
        }
    }, [isOpen, isMobile, positionDropdown]);

    const displayValue = value ? dayjs(value).format('D MMMM YYYY') : '';

    const renderDropdown = () => {
        if (!isOpen) return null;

        const calendar = (
            <CalendarDropdown
                selectedDate={value}
                onChange={handleSelect}
                minDate={min}
                maxDate={max}
                onClose={handleClose}
            />
        );

        if (isMobile) {
            return createPortal(
                <div className="cdp-picker-overlay" onClick={handleClose}>
                    <div
                        className="cdp-picker-sheet"
                        ref={dropdownRef}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="cdp-picker-sheet-handle" />
                        {calendar}
                    </div>
                </div>,
                document.body
            );
        }

        return createPortal(
            <div ref={dropdownRef} className="cdp-picker-dropdown" style={dropdownStyle}>
                {calendar}
            </div>,
            document.body
        );
    };

    return (
        <>
            <div
                ref={inputRef}
                className={`cdp-picker-input ${isOpen ? 'cdp-picker-input--open' : ''} ${disabled ? 'cdp-picker-input--disabled' : ''} ${className}`}
                onClick={handleOpen}
                role="button"
                tabIndex={disabled ? -1 : 0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleOpen(); } }}
                aria-haspopup="dialog"
                aria-expanded={isOpen}
                id={id}
            >
                <FiCalendar className="cdp-picker-icon" />
                <span className={`cdp-picker-text ${!value ? 'cdp-picker-text--placeholder' : ''}`}>
                    {displayValue || resolvedPlaceholder}
                </span>
                {value && !required && !disabled && (
                    <button
                        type="button"
                        className="cdp-picker-clear"
                        onClick={handleClear}
                        aria-label={t('datepicker.clearDate')}
                        tabIndex={-1}
                    >
                        <FiX />
                    </button>
                )}
            </div>
            {renderDropdown()}
        </>
    );
};

export default CustomDatePicker;
